<template>
  <!-- Página /video: solo el reproductor, en loop. Sin rail ni footer. -->
  <!-- Root <div> (no <main>): el selector global `main` trae la grilla del rail. -->
  <div class="video-page">
    <div class="video-page__player">
      <div :id="jwPlayerId" ref="jwPlayerContainer"></div>
    </div>
  </div>
</template>

<script>
import { fetchMedia } from './helpers/jwHelpers';
import { CLC_HOME_PAGE_TYPE } from './helpers/sidebarHelpers';

// El ID del media sale de Prismic (campo `video_loop`, pestaña Main del home).
// Esto queda como red de seguridad si el campo viene vacío o el fetch falla.
const JW_MEDIA_ID_FALLBACK = 'AY52zL4d';

// La ruta /video no tiene variante por idioma: el ID se lee del doc en inglés.
const PRISMIC_LANG = 'en-us';

export default {
  name: 'CLCVideoPage',
  data() {
    return {
      videoId: JW_MEDIA_ID_FALLBACK,
      jwPlayerInstance: null
    };
  },
  computed: {
    jwPlayerId() {
      return `jwplayer-page-${this.videoId}`;
    }
  },
  created() {
    // Sin texto en pantalla, así que el title es solo el de marca.
    document.title = 'Connected Learning Center';
    // No reactivo a propósito: solo lo lee el callback async para saber si seguimos vivos.
    this.alive = true;
  },
  async mounted() {
    await this.loadVideoId();
    // El id del contenedor depende de `videoId`: hay que esperar el re-render.
    await this.$nextTick();
    this.initJWPlayer();
  },
  methods: {
    // Campo `video_loop` del home de Prismic. Puede llegar como Key Text (string)
    // o como Rich Text (array de bloques), así que se cubren los dos.
    async loadVideoId() {
      try {
        const response = await this.$prismic.client.getSingle(CLC_HOME_PAGE_TYPE, { lang: PRISMIC_LANG });
        if (!this.alive) return;
        const field = response?.data?.video_loop;
        const id = (typeof field === 'string' ? field : field?.[0]?.text || '').trim();
        if (id) this.videoId = id;
      } catch (error) {
        console.error('METHOD: loadVideoId ==> ', error);
      }
    },
    async initJWPlayer() {
      this.destroyJWPlayer();
      if (typeof window.jwplayer === 'undefined') {
        console.error('JWPlayer library not loaded');
        return;
      }
      const { sources, image } = await fetchMedia(this.videoId);
      // El fetch es async: la vista pudo desmontarse mientras resolvía.
      if (!this.alive) return;
      if (!document.getElementById(this.jwPlayerId)) return;

      const config = {
        width: '100%',
        aspectratio: '16:9',
        // Arranca con el play del usuario: ese gesto habilita el audio (un autostart
        // sin gesto lo bloquea el browser o lo fuerza a mudo).
        autostart: false,
        // Loop: exclusivo de esta página (el VideoModal de la Home no lo usa).
        repeat: true,
        hlsjsdefault: false,
        // El póster va explícito: al armar el item a mano, sin `image` el player
        // muestra un rectángulo negro detrás del botón de play.
        playlist: sources.length
          ? [{ sources, image }]
          : `https://cdn.jwplayer.com/v2/media/${this.videoId}`
      };
      const player = window.jwplayer(this.jwPlayerId);
      if (!player || typeof player.setup !== 'function') return;
      this.jwPlayerInstance = player.setup(config);
    },
    destroyJWPlayer() {
      if (this.jwPlayerInstance) {
        try {
          this.jwPlayerInstance.remove();
        } catch (e) {
          console.warn('Error destroying JWPlayer instance:', e);
        }
        this.jwPlayerInstance = null;
      }
    }
  },
  beforeDestroy() {
    this.alive = false;
    this.destroyJWPlayer();
  }
};
</script>
